/**
 * Ported VERBATIM from `apr_template/src/components/ScoringDetailsModal.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect } from 'react';
import apiService from '../compat/api';
import './ScoringDetailsModal.css';

interface DimensionScore {
  dimension_id: string;
  dimension_name: string;
  dimension_score: number;
  component_scores: ComponentScore[];
}

interface ComponentScore {
  component_id: string;
  component_name: string;
  component_score: number;
  component_weight?: number;
  total_questions?: number;
  answered_questions?: number;
  completion_percentage?: number;
  question_scores?: QuestionScore[];
}

interface QuestionScore {
  question_id: string;
  question_text?: string;
  score: number;
  weight: number;
  weighted_score?: number;
}

interface ScoringData {
  application_id: string;
  application_name: string;
  application_id_code?: string;
  overall_score: number;
  recommendation: string;
  recommendation_color?: string;
  assessment_status?: string;
  dimension_scores: DimensionScore[];
}

interface ScoringThreshold {
  id: string;
  name: string;
  minScore: number;
  maxScore: number;
  color: string;
  description: string;
}

interface ScoringDetailsModalProps {
  applicationId: string;
  applicationName: string;
  applicationCode?: string;
  isOpen: boolean;
  onClose: () => void;
}

// Score Line Gauge Component - 5 segment approach with single color based on score
const ScoreLineGauge: React.FC<{
  score: number;
  maxScore?: number;
  label?: string;
  thresholds?: ScoringThreshold[];
}> = ({ score, maxScore = 5, label, thresholds }) => {
  const segments = 5; // 5 slices for scores 1-5

  // Get the single color for the current score (all filled segments use this color)
  const getScoreColor = (score: number): string => {
    if (thresholds && thresholds.length > 0) {
      const threshold = thresholds.find(t => score >= t.minScore && score <= t.maxScore);
      if (threshold) return threshold.color;
    }
    if (score < 2) return '#dc3545'; // Retire - Red
    if (score < 3) return '#fd7e14'; // Re-Platform - Orange
    if (score < 4) return '#ffc107'; // Re-Design - Amber
    return '#28a745'; // Retain - Green
  };

  // Calculate fill percentage for partial segment
  const getSegmentFillPercent = (segmentIndex: number, currentScore: number): number => {
    const segmentValue = segmentIndex + 1;
    if (currentScore >= segmentValue) return 100; // Fully filled
    if (currentScore <= segmentIndex) return 0; // Empty
    // Partial fill
    return (currentScore - segmentIndex) * 100;
  };

  // Single color based on the actual score
  const scoreColor = getScoreColor(score);

  return (
    <div className="score-line-gauge" style={{ width: '100%', padding: '8px 0' }}>
      {/* Label */}
      {label && (
        <div style={{
          fontSize: '13px',
          fontWeight: '500',
          color: '#333',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          {label}
        </div>
      )}

      {/* Score value */}
      <div style={{
        fontSize: '24px',
        fontWeight: '700',
        color: scoreColor,
        textAlign: 'center',
        marginBottom: '8px'
      }}>
        {score > 0 ? score.toFixed(2) : 'N/A'}
      </div>

      {/* Segmented line bar - all segments use the same color based on score */}
      <div style={{
        display: 'flex',
        gap: '3px',
        width: '100%',
        height: '16px',
        borderRadius: '4px',
        overflow: 'hidden'
      }}>
        {Array.from({ length: segments }).map((_, index) => {
          const fillPercent = getSegmentFillPercent(index, score);
          const isFilled = fillPercent > 0;

          return (
            <div
              key={index}
              style={{
                flex: 1,
                height: '100%',
                backgroundColor: '#e0e0e0',
                borderRadius: '2px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {isFilled && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: `${fillPercent}%`,
                    height: '100%',
                    backgroundColor: scoreColor, // Use single color for all filled segments
                    transition: 'width 0.3s ease, background-color 0.3s ease'
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Scale markers */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '10px',
        color: '#666',
        marginTop: '4px',
        padding: '0 2px'
      }}>
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
    </div>
  );
};

// Dial Gauge Component
const DialGauge: React.FC<{
  score: number;
  maxScore?: number;
  size?: 'large' | 'medium' | 'small';
  label?: string;
  showRecommendation?: boolean;
  recommendation?: string;
  thresholds?: ScoringThreshold[];
}> = ({ score, maxScore = 5, size = 'medium', label, showRecommendation, recommendation, thresholds }) => {
  const dimensions = {
    large: { width: 220, height: 140, strokeWidth: 16, fontSize: 32, labelSize: 14 },
    medium: { width: 160, height: 100, strokeWidth: 12, fontSize: 24, labelSize: 12 },
    small: { width: 120, height: 80, strokeWidth: 8, fontSize: 18, labelSize: 10 }
  };

  const { width, height, strokeWidth, fontSize, labelSize } = dimensions[size];
  const radius = (width - strokeWidth) / 2;
  const centerX = width / 2;
  const centerY = height - 10;

  // Calculate the arc path (180 degrees, bottom half)
  const startAngle = Math.PI;
  const endAngle = 2 * Math.PI;
  const scoreAngle = startAngle + ((score / maxScore) * Math.PI);

  // Get color based on score and thresholds
  const getScoreColor = (score: number): string => {
    if (thresholds && thresholds.length > 0) {
      const threshold = thresholds.find(t => score >= t.minScore && score <= t.maxScore);
      if (threshold) return threshold.color;
    }
    // Default colors based on score ranges
    if (score < 2) return '#dc3545'; // Retire - Red
    if (score < 3) return '#fd7e14'; // Re-Platform - Orange
    if (score < 4) return '#ffc107'; // Re-Design - Amber
    return '#28a745'; // Retain - Green
  };

  const scoreColor = getScoreColor(score);

  // Create arc path
  const createArc = (startA: number, endA: number, r: number) => {
    const startX = centerX + r * Math.cos(startA);
    const startY = centerY + r * Math.sin(startA);
    const endX = centerX + r * Math.cos(endA);
    const endY = centerY + r * Math.sin(endA);
    const largeArc = endA - startA > Math.PI ? 1 : 0;
    return `M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY}`;
  };

  // Background arc (full semicircle)
  const bgArcPath = createArc(startAngle, endAngle, radius);
  // Score arc
  const scoreArcPath = score > 0 ? createArc(startAngle, scoreAngle, radius) : '';

  return (
    <div className="dial-gauge" style={{ width, textAlign: 'center' }}>
      <svg width={width} height={height + 30} viewBox={`0 0 ${width} ${height + 30}`}>
        {/* Background arc */}
        <path
          d={bgArcPath}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Score arc */}
        {score > 0 && (
          <path
            d={scoreArcPath}
            fill="none"
            stroke={scoreColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          />
        )}
        {/* Score text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          fill={scoreColor}
          fontSize={fontSize}
          fontWeight="700"
        >
          {score > 0 ? score.toFixed(2) : 'N/A'}
        </text>
        {/* Label */}
        {label && (
          <text
            x={centerX}
            y={height + 15}
            textAnchor="middle"
            fill="#666"
            fontSize={labelSize}
            fontWeight="500"
          >
            {label}
          </text>
        )}
        {/* Min/Max labels */}
        <text x={strokeWidth / 2} y={centerY + 15} textAnchor="start" fill="#999" fontSize={10}>1</text>
        <text x={width - strokeWidth / 2} y={centerY + 15} textAnchor="end" fill="#999" fontSize={10}>5</text>
      </svg>
      {showRecommendation && recommendation && (
        <div
          className="dial-recommendation"
          style={{
            backgroundColor: scoreColor,
            color: 'white',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            marginTop: '8px',
            display: 'inline-block'
          }}
        >
          {recommendation}
        </div>
      )}
    </div>
  );
};

const ScoringDetailsModal: React.FC<ScoringDetailsModalProps> = ({
  applicationId,
  applicationName,
  applicationCode,
  isOpen,
  onClose
}) => {
  const [scoringData, setScoringData] = useState<ScoringData | null>(null);
  const [scoringThresholds, setScoringThresholds] = useState<ScoringThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDimension, setExpandedDimension] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, applicationId]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load scoring thresholds
      const thresholds = await apiService.getScoringThresholds();
      setScoringThresholds(thresholds);

      // Load scoring portfolio data
      const portfolioData = await apiService.getScoringPortfolioData();

      if (portfolioData && portfolioData.applications) {
        const appScoringData = portfolioData.applications.find((app: any) =>
          app.application_id === applicationId
        );

        if (appScoringData) {
          setScoringData({
            application_id: appScoringData.application_id,
            application_name: appScoringData.application_name,
            application_id_code: appScoringData.application_id_code,
            overall_score: appScoringData.overall_score || 0,
            recommendation: appScoringData.recommendation || 'N/A',
            recommendation_color: appScoringData.recommendation_color,
            assessment_status: appScoringData.assessment_status,
            dimension_scores: appScoringData.dimension_scores || []
          });
        } else {
          setError('No scoring data found for this application');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scoring data');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (scoringThresholds.length > 0) {
      const threshold = scoringThresholds.find(t => score >= t.minScore && score <= t.maxScore);
      if (threshold) return threshold.color;
    }
    if (score < 2) return '#C00000';
    if (score < 3) return '#ED7D31';
    if (score < 4) return '#007bff';
    return '#00B500';
  };

  const getScoreBackground = (score: number): string => {
    if (score < 2) return 'rgba(192, 0, 0, 0.1)';
    if (score < 3) return 'rgba(237, 125, 49, 0.1)';
    if (score < 4) return 'rgba(0, 123, 255, 0.1)';
    return 'rgba(0, 181, 0, 0.1)';
  };

  if (!isOpen) return null;

  return (
    <div className="scoring-modal-overlay" onClick={onClose}>
      <div className="scoring-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="scoring-modal__header">
          <div className="scoring-modal__header-content">
            <div>
              <h2 className="scoring-modal__title">Scoring Details</h2>
              <div className="scoring-modal__subtitle">
                <span className="scoring-modal__app-name">{applicationName}</span>
                {applicationCode && (
                  <span className="scoring-modal__app-code">({applicationCode})</span>
                )}
              </div>
            </div>
            <button className="scoring-modal__close" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="scoring-modal__content">
          {loading ? (
            <div className="scoring-modal__loading">
              <div className="scoring-modal__spinner"></div>
              <h3>Loading Scoring Data...</h3>
              <p>Please wait while we fetch the scoring details.</p>
            </div>
          ) : error ? (
            <div className="scoring-modal__error">
              <div className="scoring-modal__error-icon">⚠️</div>
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button className="scoring-modal__retry-btn" onClick={loadData}>
                Retry
              </button>
            </div>
          ) : scoringData ? (
            <div className="scoring-modal__data">
              {/* Score Summary - FIRST SECTION */}
              <div className="scoring-section scoring-section--summary" style={{ marginBottom: '20px' }}>
                <div className="scoring-section__header">
                  <h3>Score Summary</h3>
                </div>
                <div className="scoring-section__content">
                  <div className="score-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
                    <div className="summary-card" style={{
                      textAlign: 'center',
                      padding: '20px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="summary-value" style={{ fontSize: '32px', fontWeight: '700', color: getScoreColor(scoringData.overall_score), marginBottom: '8px' }}>
                        {scoringData.overall_score.toFixed(2)}
                      </div>
                      <div className="summary-label" style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Overall Score</div>
                    </div>
                    <div className="summary-card" style={{
                      textAlign: 'center',
                      padding: '20px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div
                        className="summary-value summary-recommendation"
                        style={{
                          fontSize: '20px',
                          fontWeight: '700',
                          color: getScoreColor(scoringData.overall_score),
                          backgroundColor: getScoreBackground(scoringData.overall_score),
                          padding: '6px 16px',
                          borderRadius: '16px',
                          display: 'inline-block',
                          marginBottom: '8px'
                        }}
                      >
                        {scoringData.recommendation}
                      </div>
                      <div className="summary-label" style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Disposition</div>
                    </div>
                    <div className="summary-card" style={{
                      textAlign: 'center',
                      padding: '20px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="summary-value" style={{ fontSize: '32px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
                        {scoringData.dimension_scores.length}
                      </div>
                      <div className="summary-label" style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Dimensions</div>
                    </div>
                    <div className="summary-card" style={{
                      textAlign: 'center',
                      padding: '20px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="summary-value" style={{ fontSize: '32px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
                        {scoringData.dimension_scores.reduce((acc, dim) =>
                          acc + (dim.component_scores?.length || 0), 0
                        )}
                      </div>
                      <div className="summary-label" style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Components</div>
                    </div>
                    <div className="summary-card" style={{
                      textAlign: 'center',
                      padding: '20px 16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div className="summary-value" style={{ fontSize: '32px', fontWeight: '700', color: '#333', marginBottom: '8px' }}>
                        {scoringData.dimension_scores.reduce((acc, dim) =>
                          acc + (dim.component_scores?.reduce((compAcc: number, comp: ComponentScore) =>
                            compAcc + (comp.question_scores?.length || comp.total_questions || 0), 0
                          ) || 0), 0
                        )}
                      </div>
                      <div className="summary-label" style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>Questions</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Overall Score Section */}
              <div className="scoring-section scoring-section--overall">
                <div className="scoring-section__header">
                  <h3>Overall Application Score</h3>
                </div>
                <div className="scoring-section__content">
                  <div className="overall-score-container">
                    {/* Left side: Dial gauge */}
                    <div className="overall-score-gauge">
                      <DialGauge
                        score={scoringData.overall_score}
                        size="large"
                        showRecommendation={true}
                        recommendation={scoringData.recommendation}
                        thresholds={scoringThresholds}
                      />
                    </div>

                    {/* Center: Recommendation Description */}
                    <div className="recommendation-description">
                      {(() => {
                        const currentThreshold = scoringThresholds.find(
                          t => scoringData.overall_score >= t.minScore && scoringData.overall_score <= t.maxScore
                        );
                        return currentThreshold ? (
                          <>
                            <h4 className="recommendation-description__title">
                              Why {currentThreshold.name} {applicationCode || ''} {applicationName}?
                            </h4>
                            <p className="recommendation-description__text">
                              {currentThreshold.description}
                            </p>
                          </>
                        ) : null;
                      })()}
                    </div>

                    {/* Right side: Compact Score Scale */}
                    <div className="score-legend score-legend--compact">
                      <h4>Score Scale</h4>
                      <div className="legend-items">
                        {scoringThresholds.map((threshold) => (
                          <div
                            key={threshold.id}
                            className={`legend-item ${scoringData.recommendation === threshold.name ? 'legend-item--active' : ''}`}
                          >
                            <span
                              className="legend-color"
                              style={{ backgroundColor: threshold.color }}
                            ></span>
                            <span className="legend-label">{threshold.name}</span>
                            <span className="legend-range">
                              {threshold.minScore.toFixed(1)}-{threshold.maxScore.toFixed(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dimension Scores Section */}
              <div className="scoring-section scoring-section--dimensions">
                <div className="scoring-section__header">
                  <h3>Score by Dimension</h3>
                  <span className="scoring-section__count">
                    {scoringData.dimension_scores.length} Dimensions
                  </span>
                </div>
                <div className="scoring-section__content">
                  <div className="dimension-gauges" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                    {scoringData.dimension_scores.map((dimension) => (
                      <div
                        key={dimension.dimension_id}
                        className={`dimension-gauge-card ${expandedDimension === dimension.dimension_id ? 'expanded' : ''}`}
                        onClick={() => setExpandedDimension(
                          expandedDimension === dimension.dimension_id ? null : dimension.dimension_id
                        )}
                        style={{
                          backgroundColor: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          cursor: 'pointer',
                          transition: 'box-shadow 0.2s, border-color 0.2s'
                        }}
                      >
                        <ScoreLineGauge
                          score={dimension.dimension_score}
                          label={dimension.dimension_name.replace(/^[A-Z]+ - /, '')}
                          thresholds={scoringThresholds}
                        />
                        <div className="dimension-expand-hint" style={{
                          textAlign: 'center',
                          fontSize: '12px',
                          color: '#666',
                          marginTop: '8px'
                        }}>
                          {expandedDimension === dimension.dimension_id ? '▲ Hide Components' : '▼ Show Components'}
                        </div>

                        {/* Component Details (expandable) */}
                        {expandedDimension === dimension.dimension_id && dimension.component_scores && (
                          <div className="component-details" style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                            {dimension.component_scores.map((component: ComponentScore) => (
                              <div key={component.component_id} className="component-card" style={{
                                backgroundColor: '#f9f9f9',
                                borderRadius: '6px',
                                padding: '12px',
                                marginBottom: '12px'
                              }}>
                                <div className="component-card__header" style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '8px',
                                  gap: '8px'
                                }}>
                                  <span className="component-card__name" style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                                    {component.component_name.replace(/^[A-Z]+ - /, '')}
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {typeof component.component_weight === 'number' && (
                                      <span
                                        className="component-card__weight"
                                        style={{
                                          color: '#555',
                                          backgroundColor: '#eee',
                                          padding: '2px 8px',
                                          borderRadius: '10px',
                                          fontWeight: '500',
                                          fontSize: '11px'
                                        }}
                                      >
                                        Weight: {component.component_weight}
                                      </span>
                                    )}
                                    <span
                                      className="component-card__score"
                                      style={{
                                        color: getScoreColor(component.component_score),
                                        backgroundColor: getScoreBackground(component.component_score),
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontWeight: '600',
                                        fontSize: '13px'
                                      }}
                                    >
                                      {component.component_score.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                                {/* Question Details */}
                                {component.question_scores && component.question_scores.length > 0 && (
                                  <div className="question-list" style={{ marginTop: '8px' }}>
                                    <div className="question-list__header" style={{
                                      display: 'grid',
                                      gridTemplateColumns: '1fr 60px 60px',
                                      gap: '8px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      color: '#666',
                                      padding: '6px 0',
                                      borderBottom: '1px solid #ddd',
                                      marginBottom: '4px'
                                    }}>
                                      <span>Question</span>
                                      <span style={{ textAlign: 'center' }}>Weight</span>
                                      <span style={{ textAlign: 'center' }}>Score</span>
                                    </div>
                                    {component.question_scores.map((q: QuestionScore) => (
                                      <div key={q.question_id} className="question-item" style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 60px 60px',
                                        gap: '8px',
                                        alignItems: 'center',
                                        padding: '8px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                      }}>
                                        <div className="question-item__text" style={{
                                          fontSize: '12px',
                                          color: '#333',
                                          lineHeight: '1.4',
                                          overflow: 'hidden',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical' as 'vertical'
                                        }}>
                                          {q.question_text || '-'}
                                        </div>
                                        <span className="question-item__weight" style={{
                                          textAlign: 'center',
                                          fontSize: '12px',
                                          color: '#666'
                                        }}>
                                          {q.weight}
                                        </span>
                                        <span
                                          className="question-item__score"
                                          style={{
                                            textAlign: 'center',
                                            color: getScoreColor(q.score),
                                            backgroundColor: getScoreBackground(q.score),
                                            padding: '2px 8px',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            fontSize: '12px'
                                          }}
                                        >
                                          {q.score}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          ) : (
            <div className="scoring-modal__empty">
              <div className="scoring-modal__empty-icon">📋</div>
              <h3>No Scoring Data</h3>
              <p>This application has not been scored yet.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="scoring-modal__footer">
          <div className="scoring-modal__footer-info">
            {scoringData && scoringData.assessment_status && (
              <span className={`status-badge status-badge--${scoringData.assessment_status.toLowerCase().replace(' ', '-')}`}>
                {scoringData.assessment_status}
              </span>
            )}
          </div>
          <button className="scoring-modal__close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoringDetailsModal;
